// Centralized portfolio data — single source of truth.
// Ported verbatim from the design bundle's data.jsx.

export type VocabLevel = "shipped" | "used" | "studied";
export type ArtVariant = "rings" | "grid" | "waves" | "nodes" | "agents" | "edge" | "tokens" | "quantum" | "wave";

export interface Portfolio {
  identity: {
    name: string;
    handle: string;
    role: string;
    location: string;
    email: string;
    tagline: string;
    links: {
      github: string;
      linkedin: string;
      x: string;
      medium: string;
      substack: string;
      mail: string;
      scholar: string;
      cv: string;
    };
  };
  about: string[];
  journey: { year: string; title: string; note: string }[];
  loves: { name: string; icon: string }[];
  outside: { icon: string; label: string; value: string }[];
  now: string[];
  experience: {
    role: string;
    org: string;
    dates: string;
    location: string;
    bullets: string[];
    tags?: string[];
    href?: string;
  }[];
  projects: {
    slug: string;
    name: string;
    kicker: string;
    blurb: string;
    details: string[];
    stack: string[];
    year: string;
    href: string;
    playStore?: string;
    art: ArtVariant;
  }[];
  publications: {
    title: string;
    authors: string;
    venue: string;
    year: string;
    bibkey: string;
    summary: string;
    doi?: string;
    note?: string;
    href?: string;
  }[];
  talks: { title: string; venue: string; date?: string; where?: string; note?: string; year?: string; href?: string }[];
  awards: { title: string; note: string; year: string; image?: string }[];
  posts: {
    title: string;
    kicker: string;
    date: string;
    readtime: string;
    tag: string;
    href: string;
    image?: string;
  }[];
  // Articles that aren't on Medium (LinkedIn pulse, Substack, etc.) — they
  // can't be live-fetched from a browser-only context, so they live here as
  // static entries and get merged into the Writing section alongside Medium.
  extraPosts: {
    title: string;
    kicker: string;
    date: string;
    readtime: string;
    tag: string;
    href: string;
    image?: string;
  }[];
  engineering: {
    stack: { group: string; reach: string[]; regular: string[]; ambient: string[] }[];
    systemSketch: { title: string; subtitle: string; diagram: string; notes: string[] };
    latency: {
      title: string;
      rows: { stage: string; p50: string; p95: string }[];
      total: { p50: string; p95: string };
    };
    vocab: { term: string; level: VocabLevel; note: string }[];
    principles: { n: string; title: string; body: string }[];
  };
  reading: {
    nowReading: { title: string; author: string; note?: string }[];
    influences: { title: string; author: string }[];
    listening: string[];
  };
}


const CV = `${import.meta.env.BASE_URL}Mohan-Krishna-Resume.pdf`;

export const portfolio: Portfolio = {
  identity: {
    name: "Mohan Krishna G R",
    handle: "MohanKrishnaGR",
    role: "AI/ML Research Engineer",
    location: "Coimbatore, India · Open to relocate",
    email: "mohankrishnagr08@gmail.com",
    tagline: "I make systems scale, AI models smaller, faster, and more efficient - and I also write about why.",
    links: {
      github: "https://github.com/MohanKrishnaGR",
      linkedin: "https://www.linkedin.com/in/grmk/",
      x: "https://x.com/mohan_speaks_",
      medium: "https://medium.com/@mohankrishnagr08",
      substack: "https://mohankrishnagr.substack.com/",
      mail: "mailto:mohankrishnagr08@gmail.com",
      scholar: "https://scholar.google.com/citations?user=8WrHWtYAAAAJ&hl=en",
      cv: CV,
    },
  },

  about: [
    "I'm Mohan Krishna G R. I write code that scales efficiently and reasons though. I started by writing code that just printed “Hello, World!” Somewhere between that first C program and a late-night bug-hunt during my second semester, the question quietly shifted from “does this run?” to “why does this run faster than the version next to it?”. That curiosity has been the through-line ever since.",
    "I'm a final-year M.Tech (Integrated, CS) student at Sri Ramakrishna Engineering College, where I've held the highest CGPA in my cohort for all the consecutive years. I cut my teeth in competitive coding circles and the hackathon culture on campus and in industries. MindWave (a real-time mental-health classifier in Flutter) won the CII Ultimate Hackathon; AgenticEye picked up a Top 5 Paper Award at Airbus TechXceed against 350+ engineers. Most of what I know about distributed systems, edge inference, and shipping under a deadline, I learned in those requirements and uncertainities.",
    "Along the way I picked up the usual stack Python and C++ first, then JavaScript and TypeScript, Rust for things that need to be fast, CUDA and llama.cpp for things that need to be even faster, and PyTorch + Transformers for things that need to think. Java for competitive promgramming and highly reliable systems. Today I sit at the intersection of retrieval, reasoning, and the engineering it takes to ship them. Previously researching Knowledge graph, GraphRAG, CV pipelines and Document-AI at Airbus.",
    "I care about systems that are honest about what they know. That means GraphRAG and causal retrieval over plain-vanilla embeddings, evaluation that survives contact with reality, and inference stacks that don't fold when you put real traffic through them. Long-term, I'm working toward a research career in efficient, reliable AI systems - quantization, agent orchestration, and the safety guardrails that make them deployable. I'm applying to full time roles in Fall 2027.",
    "Outside the terminal, I write on Medium, read more research papers, I'm a news junkie too, I love editorials though. I have a slightly evangelistic love of Material 3 and clean design principles. My driving principle is simple: build the eval first, ship the honest version, then make it faster.",
  ],

  journey: [
    { year: "School", title: "First “Hello, World!”", note: "C, then C++. The spark." },
    { year: "2022", title: "M.Tech (Integrated, CS) begins", note: "Sri Ramakrishna Engineering College." },
    { year: "2023", title: "CII Ultimate Hackathon - Winner", note: "MindWave: real-time mental-health classifier." },
    { year: "2024", title: "Honeywell Drones Technology Hackathon - Runner-up", note: " AI powered UAV for fire detection and mission critical operations." },
    { year: "2024", title: "AI/ML Intern · Infosys Springboard", note: "564K-record transformer summarization." },
    { year: "2024", title: "First IEEE publication", note: "Stress prediction via hybrid ML." },
    { year: "2025", title: "Trainee SWE · SuperDNA 3D Lab", note: "CV pipelines · UniHair research." },
    { year: "2025", title: "Generative AI Intern · Airbus", note: "GraphRAG · Document-AI · 2× inference speed-up." },
    { year: "2025", title: "Top 5 Paper Award · Airbus TechXceed", note: "Among 350+ engineers - AgenticEye." },
    { year: "2025", title: "Speaker · Airbus Global AI Week", note: "Causal GraphRAG for FAL root-cause analysis." },
    { year: "2026", title: "AI Engineer Intern · Valura.ai", note: "Agentic systems for asset management." },
    { year: "2027", title: "Target: Full time AI/ML Engineering roles", note: "Efficient & reliable LLM systems." },
  ],

  loves: [
    { name: "PyTorch", icon: "psychology" },
    { name: "Transformers", icon: "hub" },
    { name: "FastAPI", icon: "bolt" },
    { name: "Rust", icon: "settings" },
    { name: "Go", icon: "speed" },
    { name: "llama.cpp", icon: "memory" },
    { name: "Postgres + pgvector", icon: "database" },
    { name: "Material 3", icon: "palette" },
    { name: "Linux", icon: "terminal" },
    { name: "Tmux + Neovim", icon: "code" },
    { name: "JetBrains Mono", icon: "text_fields" },
    { name: "Keyboard-first UX", icon: "keyboard" },
  ],

  outside: [
    { icon: "edit_note", label: "Writing", value: "Long-form on Medium — RAG, retrieval, LLMs.  " },
    { icon: "menu_book", label: "Reading", value: "Papers, blogs, the occasional Kleppmann re-read." },
    { icon: "groups", label: "Mentoring", value: "Hackathon judging & student-team mentoring on campus." },
    { icon: "design_services", label: "Design", value: "Material 3 enthusiast. Clean and expressive design." },
  ],

  now: [
    "Agentic systems for asset management at Valura.ai (2Cents Capital).",
    "Reading: ‘Textbooks Are All You Need’ (Phi-1), DSPy compilers, Constitutional AI revisions.",
    "Writing a post on why your RAG eval is probably lying to you.",
  ],

  experience: [
    {
      role: "Generative AI Intern",
      org: "Airbus",
      dates: "Jun 2025 — Dec 2025",
      location: "Bengaluru, IN",
      bullets: [
        "Researched GraphRAG / LLM systems that improved retrieval by 25%; built agentic Document-AI pipelines (40% automation gains); optimized transformer inference for a 2× speed-up in production.",
        "Top 5 Paper Award at Airbus TechXceed 2025 (among 350+ engineers) for AgenticEye — agentic document extraction for Stress Dossiers.",
        "Selected speaker at Airbus Global AI Week 2025: a Causal GraphRAG framework for explainable root-cause analysis in Final Assembly Line NC workflows.",
      ],
      tags: ["GraphRAG", "Agentic", "Inference Optimization"],
      href: "https://drive.google.com/file/d/1BDOO6ARom3XLrmrAGWxxqQuxiZYr_dpn/view",
    },
    {
      role: "AI Engineer Intern",
      org: "2Cents Capital · Valura.ai",
      dates: "Jan 2026 — Feb 2026",
      location: "Dubai, UAE (Remote)",
      bullets: [
        "Engineering Agentic AI systems for investment research and asset-management decisioning.",
      ],
      tags: ["Agentic AI", "Research", "Finance"],
      href: "https://drive.google.com/file/d/1sH95UJECUxazJkYOrUcD1ToOmr444DZn/view?usp=sharing",
    },
    {
      role: "Trainee Software Engineer",
      org: "SuperDNA 3D Lab",
      dates: "Jan 2025 — Mar 2025",
      location: "Part-time, Remote",
      bullets: [
        "Built CV pipelines for automated QA of AI-generated e-commerce imagery (15-rule rubric).",
        "Monocular dimension estimation with depth transformers; experimental 3D hair reconstruction (UniHair).",
      ],
      tags: ["Computer Vision", "Depth", "3D"],
      href: "https://drive.google.com/file/d/1-qZgq6s-ogqOhUBJ2xMhgoXL9bXoJodB/view?usp=drive_link",
    },
    {
      role: "AI/ML Intern",
      org: "Infosys Springboard",
      dates: "May 2024 — Jul 2024",
      location: "Summer, Remote",
      bullets: [
        "Research-grade text summarization on 564K+ records: transformer fine-tuning (300% ROUGE-2 gain) + a novel TF-IDF / KMeans extractive baseline.",
        "Reproducible experiments deployed on Azure.",
      ],
      tags: ["NLP", "Summarization", "Azure"],
      href: "https://infyspringboard.onwingspan.com/public-assets/infosysheadstart/cert/lex_auth_01413406637122355223/1-1e5e305b-86d1-40e6-81a2-43278ce144b4.pdf",
    },
  ],

  projects: [
    {
      slug: "BookDNA",
      name: "BookDNA",
      kicker: "Offline-First Reading Intelligence Platform",
      blurb: "AI-powered mobile app that transforms physical bookshelves into a searchable digital library with reading analytics, knowledge graphs, and an intelligent reading companion.",
      details: [
        "Built an offline-first Flutter app with ISBN barcode scanning, resilient 3-tier metadata retrieval, reading tracker, and 20+ personalized reading insights.",
        "Engineered a Drift + Supabase sync engine with last-writer-wins conflict resolution, RLS, soft-delete tombstones, and seamless offline synchronization.",
        "Integrated Google Gemini through Deno Edge Functions for Library GPT, AI shelf analysis, and interactive knowledge graphs; automated Play Store releases with GitHub Actions."
      ],
      stack: [
        "Flutter",
        "Riverpod",
        "SQLite (Drift)",
        "Supabase",
        "PostgreSQL",
        "Deno Edge Functions",
        "Firebase",
        "GitHub Actions",
        "Gemini"
      ],
      year: "2026",
      href: "https://mohankrishnagr.github.io/BookDNA/index.html",
      playStore: "https://play.google.com/store/apps/details?id=com.bookdna.bookdna",
      art: "waves"
    },
    {
      slug: "transformertax",
      name: "TransformerTax",
      kicker: "State-Space vs Transformer · Dense Retrieval Benchmark",
      blurb: "Empirical study of state-space (Mamba) versus transformer encoders in dense retrieval — head-to-head on retrieval quality, throughput, and the trade-offs nobody quite measures honestly.",
      details: [
        "Side-by-side bi-encoder training: Mamba SSM vs BERT under matched parameter budgets, training data, and contrastive recipe.",
        "Evaluation across MRR / nDCG with FAISS-backed indexes; ablations isolate sequence-length scaling and attention-vs-SSM contribution.",
        "GPU-optimized loop with torch.compile + Flash Attention 2; reproducible, seed-controlled runs.",
      ],
      stack: ["PyTorch", "CUDA", "Transformers", "FAISS", "BERT", "Mamba SSM", "Dense Retrieval", "MRR / nDCG", "torch.compile", "Flash Attention 2"],
      year: "2026",
      href: "https://mohankrishnagr.github.io/TransformerTax/",
      art: "nodes",
    },
    {
      slug: "depth-ablation",
      name: "Depth Ablation",
      kicker: "BERT Bi-Encoder Depth Study · Zero-Shot BEIR Transfer",
      blurb: "Systematic study of encoder depth (L3–L12) on zero-shot BEIR transfer from MS MARCO. 20 models trained (4 depths × 5 experimental configurations) under controlled seeds and matched training budgets.",
      details: [
        "Reproducible ablation across nfcorpus, scifact, and fiqa with full per-seed, per-benchmark nDCG analysis.",
        "Bf16/fp16 mixed-precision training with in-batch-negatives ranking loss and AdamW; clean experiment matrix from a single config tree.",
        "Open data + code for the IR community: ir-measures evaluation, NumPy / Pandas analysis, Matplotlib reporting.",
      ],
      stack: ["PyTorch", "Transformers", "Sentence-Transformers", "BEIR", "BERT", "Bi-encoder", "MS MARCO", "Zero-shot Transfer", "ir-measures", "AdamW", "In-batch Negatives", "Reproducible ML", "CUDA"],
      year: "2026",
      href: "https://mohankrishnagr.github.io/bert-bi-encoder-depth-ablation/",
      art: "grid",
    },
    {
      slug: "certushire",
      name: "CertusHire",
      kicker: "Autonomous Multi-Agent Interview Platform",
      blurb: "Production-grade multi-agent LLM system that runs an entire technical interview — JD/CV parsing, adaptive voice questioning, and automated evaluation. Reduces manual screening 90%.",
      details: [
        "DAR3 — a state-machine orchestration protocol coordinating interviewer & evaluator agents, dynamically adjusting question difficulty against real-time candidate performance.",
        "Hybrid dense + sparse RAG (embeddings + TF-IDF) grounding generation in verified sources. 85% agreement with human interviewer scores.",
        "Scaled to 100+ concurrent sessions on FastAPI + Celery/RabbitMQ, <200ms latency. React/WebSocket frontend with Constitutional-AI safety guardrails.",
      ],
      stack: ["Python", "FastAPI", "LangChain", "React", "TypeScript", "PostgreSQL", "Celery", "RabbitMQ", "Docker", "llama.cpp", "RAG"],
      year: "2025",
      href: "https://github.com/MohanKrishnaGR",
      art: "agents",
    },
    {
      slug: "pyroguardian",
      name: "PyroGuardian",
      kicker: "UAV Fire-Detection · Honeywell Hackathon (Runner-Up)",
      blurb: "TensorRT-optimized fire detection on Jetson Nano. 86.7M parameters, FP16, 30 FPS @ 720p, 90% faster inference than the PyTorch FP32 baseline.",
      details: [
        "Engineered a 51 GB dataset, 10K+ annotated frames across 8 fire scenarios with multi-scale augmentations.",
        "AWS SNS alerts with RBAC; <1s latency for 500+ users; dynamic prioritization via severity scores.",
        "40% faster fire-response time vs thermal sensors; 20% fewer false alarms than traditional video analytics.",
      ],
      stack: ["PyTorch", "TensorRT", "CUDA", "NVIDIA Jetson Nano", "NVIDIA TAO / NGC", "AWS SNS", "OpenCV", "FastAPI", "Docker", "HTML", "CSS", "JS"],
      year: "2024",
      href: "https://github.com/MohanKrishnaGR",
      art: "edge",
    },
    {
      slug: "textsumm",
      name: "TextSumm",
      kicker: "Hybrid Abstractive + Extractive Summarization",
      blurb: "End-to-end summarization for PDFs, DOCX, URLs and raw text. 300% ROUGE-2 lift via hybrid pipeline: fine-tuned transformers + a novel TF-IDF / KMeans extractive head.",
      details: [
        "ROUGE-1 of 61.32 on 550K+ records; FastAPI multi-format ingest.",
        "Docker on Azure ACI, GitHub Actions CI/CD, 99.9% uptime.",
      ],
      stack: ["Python", "NLP", "Transformers", "FastAPI", "Docker", "Azure", "CI/CD", "HTML", "CSS", "JS"],
      year: "2024",
      href: "https://github.com/MohanKrishnaGR",
      art: "tokens",
    },
    {
      slug: "quantum",
      name: "Crop Schedule Management",
      kicker: "Quantum Annealing · QUBO · D-Wave",
      blurb: "Formulated crop scheduling as a QUBO and solved it on D-Wave. 50% lower computational overhead vs classical solvers, 95% field utilization in simulation.",
      details: [
        "Automated rotations for 10+ crop types across 100+ fields, respecting adjacency and resource constraints.",
        "30% yield-prediction improvement over traditional methods.",
        "Guided by SREC & CQuICC, IIT Madras.",
      ],
      stack: ["D-Wave", "QUBO", "Quantum Annealing", "Python"],
      year: "2024",
      href: "https://github.com/MohanKrishnaGR",
      art: "quantum",
    },
    {
      slug: "mindwave",
      name: "MindWave",
      kicker: "Real-time Mental-Health AI · CII Ultimate Hackathon (Winner)",
      blurb: "5-person team. Hybrid ML model for stress prediction (94% accuracy, 5-fold CV). Real-time inference inside a Flutter mobile app.",
      details: [
        "TensorFlow Serving with async I/O; <500 ms response under concurrent load.",
        "35% engagement lift tracked via Firebase Analytics.",
      ],
      stack: ["Python", "TensorFlow", "Flutter", "REST"],
      year: "2023",
      href: "https://github.com/MohanKrishnaGR",
      art: "wave",
    },
  ],

  publications: [
    {
      title: "Indian Rainfall Prediction Using Machine Learning: A Comparative Study",
      authors: "Mohan Krishna G R et al.",
      venue: "IConSCEPT 2024, NIT Puducherry (Karaikal) · IEEE Xplore",
      year: "2024",
      bibkey: "krishna2024rainfall",
      summary:
        "Comparative study of SVM, Naive Bayes, KNN, and Decision Trees on Indian meteorological data — 88% accuracy with SVM, 95% precision on monsoon prediction.",
      doi: "10.1109/IConSCEPT…",
      href: "https://ieeexplore.ieee.org/document/10627820",
    },
  ],

  talks: [
    {
      title: "Causal GraphRAG for Explainable Root-Cause Analysis",
      venue: "Airbus Global AI Week 2025",
      date: "Nov 2025",
      year: "2025",
      where: "Bengaluru, IN",
      note: "Selected speaker — framework for explainable RCA in Final Assembly Line non-conformance workflows.",
      href: "https://drive.google.com/file/d/1CNdfSyVCeTG7PklIXgYkIARwqwNYbBts/view",
    },
    {
      title: "AgenticEye: Agentic Document Extraction for Stress Dossiers",
      venue: "Airbus TechXceed 2025",
      date: "Oct 2025",
      year: "2025",
      where: "Bengaluru, IN",
      note: "Top 5 Paper Award among 350+ engineers.",
      href: "https://drive.google.com/drive/folders/1qaNki8R_z88-y5anvpBfcG3az7DrXFRi",
    },
    {
      title: "GraphRAG — Engineering Structured, Trustworthy LLM Systems",
      venue: "SRIT, Anna University",
      year: "2025",
      note: "Delivered an invited lecture on GraphRAG, framing LLM hallucination and reasoning failures as data-structure problems and presenting graph-grounded retrieval, traversal, and integration as a principled architecture for auditable, multi-hop LLM systems.",
      href: "https://drive.google.com/drive/folders/1WDYj1lqaG4DvhtRk1CV-qrjrHG8abMnQ",
    },
  ],

  awards: [
    { title: "Sri. P. Ramaswamy Memorial Award", note: "Highest CGPA, A.Y. 2022–2025 (3 years running). Top 0.3% of 4,400+ students.", year: "2023–25", image: "awards/CGPA.jpg" },
    { title: "Top 5 Paper Award", note: "Airbus TechXceed 2025, among 350+ engineers (AgenticEye).", year: "2025", image: "awards/TechXceed.jpg" },
    { title: "Honeywell Drone Tech Hackathon — Runner-Up", note: "PyroGuardian. ₹25K, 50+ teams.", year: "2024", image: "awards/Honeywell.jpg" },
    { title: "CII Ultimate Hackathon — Overall Winner", note: "MindWave. ₹25K, 1000+ teams.", year: "2023", image: "awards/CII.jpg" },
    { title: "Student Innovation Ambassador", note: "Selected 7 of 4,400 — mentored 50+ on AI/ML projects.", year: "2023", image: "awards/CoIN.jpg" },
    { title: "Best Project Award", note: "Crop Schedule Management (Quantum), A.Y. 2023–24.", year: "2024", image: "awards/Quantum.jpeg" },
    { title: "Best Innovation Idea, InnoTech 2023", note: "UAV-pest-detection / IoT soil analytics platform.", year: "2023", image: "awards/InnoTech.jpeg" },
    { title: "Head, Blog Writers @ SREC", note: "Led 80+ member team. +40% campus engagement.", year: "2023" },
  ],

  posts: [
    {
      title: "The State of Information Retrieval in 2026",
      kicker: "From BM25 to reasoning-augmented agentic search — a guide to a field that quietly turned into something else.",
      date: "Apr 2026",
      readtime: "12 min",
      tag: "Retrieval",
      href: "https://medium.com/@mohankrishnagr08/the-state-of-information-retrieval-in-2026-192f125a5269",
      image: "https://miro.medium.com/v2/resize:fill:800:540/1*FIsH8dyjOCcB9NKu9hFr9g.jpeg",
    },
    {
      title: "The Future of Generative AI Engineering Lies Beyond Bigger Models",
      kicker: "Scaling laws hit a wall sooner than anyone advertised. Notes on the engineering shift from training bigger to building smarter.",
      date: "Sep 2025",
      readtime: "10 min",
      tag: "GenAI",
      href: "https://medium.com/@mohankrishnagr08/the-future-of-generative-ai-engineering-lies-beyond-bigger-models-847d4d517ebc",
      image: "https://miro.medium.com/v2/resize:fill:800:540/1*vsxMcio8Yp5nj4sf-sTgBw@2x.jpeg",
    },
    {
      title: "Overcoming Analysis Paralysis",
      kicker: "On indecision, the cost of perfect information, and why the smallest action almost always beats the best plan.",
      date: "Nov 2024",
      readtime: "6 min",
      tag: "Notes",
      href: "https://medium.com/@mohankrishnagr08/overcoming-analysis-paralysis-61b151f00f80",
      image: "https://miro.medium.com/v2/resize:fill:800:540/1*3Qv7zOZ5JKxDJ6Mu4J-ftw.jpeg",
    },
    {
      title: "My Journey Through Chaos",
      kicker: "Contemplating the chaotic world around us — the buzz, the noise, and the practice of choosing a signal worth following.",
      date: "Oct 2024",
      readtime: "7 min",
      tag: "Notes",
      href: "https://medium.com/@mohankrishnagr08/my-journey-through-choas-b0f3d5cf7590",
      image: "https://miro.medium.com/v2/resize:fill:800:540/1*NtMqB3lq61_p8oGSCIw_1Q.jpeg",
    },
    {
      title: "Engineering the Future & The Innovation Imperative",
      kicker: "How the next decade of engineering gets built — and what stops mattering when the constraints change underneath you.",
      date: "Aug 2024",
      readtime: "8 min",
      tag: "Essay",
      href: "https://medium.com/@mohankrishnagr08/engineering-the-future-the-innovation-imperative-a0740d5217f4",
      image: "https://miro.medium.com/v2/resize:fill:800:540/1*1BTVnh86tePny7BeETWS5A.jpeg",
    },
    {
      title: "Forging Your Legacy — embracing the long road to lasting success",
      kicker: "Short-term pleasure vs. long-arc compounding. A note to the version of myself that was about to take the easier path.",
      date: "Aug 2023",
      readtime: "5 min",
      tag: "Essay",
      href: "https://medium.com/@mohankrishnagr08/forging-your-legacy-embracing-the-long-road-to-lasting-success-1040c5da7a7",
      image: "https://miro.medium.com/v2/resize:fill:800:540/1*OWkwA9EdpwS5DktPG5dQIA.jpeg",
    },
  ],

  // Non-Medium articles — always shown, prepended to the live Medium feed.
  // To add an image: either paste the article's cover image URL here (right-
  // click → "Copy image address" on the LinkedIn cover) or save the image to
  // `public/posts/<slug>.jpg` and reference it as `posts/<slug>.jpg` (Vite's
  // BASE_URL is prepended automatically by the Writing component).
  extraPosts: [
    {
      title: "AI in Manufacturing",
      kicker: "Most AI projects in manufacturing don't fail because the model is wrong - they fail because the problem is wrong. Notes on vision QA, predictive maintenance, and what physical AI changes next.",
      date: "May 2026",
      readtime: "4 min",
      tag: "LinkedIn",
      href: "https://www.linkedin.com/pulse/ai-manufacturing-mohan-krishna-g-r-xxgac/",
      image: "article/AI_In_Manufacturing.png",
    },
  ],

  engineering: {
    stack: [
      {
        group: "Languages",
        reach: ["Python", "TypeScript"],
        regular: ["Rust", "Go", "C++", "SQL"],
        ambient: ["CUDA", "Bash", "Lua", "JavaScript", "Dart", "Haskell", "Solidity"],
      },
      {
        group: "Inference & ML systems",
        reach: ["PyTorch", "Transformers"],
        regular: ["llama.cpp", "vLLM", "TensorRT", "Triton", "ONNX", "FAISS"],
        ambient: ["DeepSpeed", "Flash-Attn", "torch.compile"],
      },
      {
        group: "Distributed & async",
        reach: ["FastAPI", "Celery"],
        regular: ["RabbitMQ", "Redis Streams", "Kafka", "gRPC", "WebSockets", "asyncio"],
        ambient: ["Ray", "Temporal", "NATS"],
      },
      {
        group: "Data & retrieval",
        reach: ["Postgres + pgvector"],
        regular: ["Neo4j", "Weaviate", "MySQL", "SQLite", "DuckDB", "ClickHouse", "S3"],
        ambient: ["Polars / Arrow", "Parquet", "Iceberg"],
      },
      {
        group: "Web & mobile",
        reach: ["React.js", "Next.js"],
        regular: ["Node.js", "Express.js", "Flask", "Django", "Flutter", "TailwindCSS", "Material 3", "HTML5", "CSS3"],
        ambient: ["Vue.js", "AngularJS", "Bootstrap", "Android"],
      },
      {
        group: "Infra & deploy",
        reach: ["Docker", "GitHub Actions"],
        regular: ["AWS (ECS · SNS · SQS)", "Azure (ACI)", "Nginx", "Linux"],
        ambient: ["Terraform", "Kubernetes", "Prometheus + Grafana", "OpenTelemetry"],
      },
    ],

    systemSketch: {
      title: "CertusHire · one interview turn",
      subtitle: "Candidate answer in → grounded next question out, under 200 ms.",
      diagram:
        "  ┌─────────────┐    ws    ┌─────────────┐\n" +
        "  │  candidate  │ ───────▶ │  FastAPI    │\n" +
        "  │  browser    │ ◀─────── │  gateway    │\n" +
        "  └─────────────┘          └──────┬──────┘\n" +
        "                                  │ enqueue · idempotency-key\n" +
        "                                  ▼\n" +
        "                          ┌──────────────┐\n" +
        "                          │  RabbitMQ    │  ◀── DLQ + retry policy\n" +
        "                          └──────┬───────┘\n" +
        "                                 ▼\n" +
        "                          ┌──────────────┐      ┌──────────────┐\n" +
        "                          │   Celery     │ ───▶ │  Postgres    │\n" +
        "                          │   workers    │      │  + pgvector  │\n" +
        "                          └──────┬───────┘      └──────────────┘\n" +
        "                                 ▼\n" +
        "                          ┌──────────────┐      ┌──────────────┐\n" +
        "                          │   llama.cpp  │ ───▶ │   evaluator  │\n" +
        "                          │   (LLM)      │      │   agent      │\n" +
        "                          └──────────────┘      └──────────────┘",
      notes: [
        "DAR3 state machine pins question difficulty against a running skill estimate; the evaluator agent owns the transition.",
        "Hybrid retrieval (BM25 + dense) fans out concurrently and merges before generation — the LLM never sees ungrounded context.",
        "Everything inference-side is llama.cpp. Fewer moving parts, more deterministic tail latency, fewer 3 a.m. pages.",
      ],
    },

    latency: {
      title: "Latency budget · one interview turn",
      rows: [
        { stage: "WebSocket ingress + validate", p50: "4 ms", p95: "9 ms" },
        { stage: "Embed candidate utterance", p50: "18 ms", p95: "32 ms" },
        { stage: "Hybrid retrieval (BM25 + dense)", p50: "22 ms", p95: "41 ms" },
        { stage: "LLM generation (next question)", p50: "112 ms", p95: "178 ms" },
        { stage: "Evaluator update + persist", p50: "14 ms", p95: "26 ms" },
        { stage: "WebSocket egress", p50: "3 ms", p95: "6 ms" },
      ],
      total: { p50: "173 ms", p95: "292 ms" },
    },

    vocab: [
      { term: "Backpressure", level: "shipped", note: "Celery + RabbitMQ flow control" },
      { term: "Idempotency keys", level: "shipped", note: "Retry-safe agent steps" },
      { term: "At-least-once delivery", level: "shipped", note: "Dedup at the consumer" },
      { term: "Saga pattern", level: "shipped", note: "Compensating txns across agents" },
      { term: "Circuit breakers", level: "shipped", note: "On every outbound LLM call" },
      { term: "Token-bucket rate-limit", level: "shipped", note: "Per-tenant LLM quotas" },
      { term: "Tail latency (p99)", level: "shipped", note: "The number I actually optimize" },
      { term: "Cold-start mitigation", level: "shipped", note: "Pre-warmed worker pools" },
      { term: "Consistent hashing", level: "used", note: "Sharding embedding caches" },
      { term: "Quorum reads / writes", level: "used", note: "RF=3, W=2, R=2" },
      { term: "Lease / fencing tokens", level: "used", note: "Stops split-brain workers" },
      { term: "Bloom filters", level: "used", note: "Negative cache for dedup" },
      { term: "Write-ahead logs", level: "used", note: "Postgres internals reading group" },
      { term: "Leader election (Raft)", level: "used", note: "Read Ongaro before writing one" },
      { term: "Exactly-once semantics", level: "studied", note: "Mostly a marketing claim" },
      { term: "Two-phase commit", level: "studied", note: "Avoid unless you must" },
      { term: "CRDTs", level: "studied", note: "For live-collab UIs" },
      { term: "Watermarks (stream)", level: "studied", note: "Flink mental model" },
    ],

    principles: [
      { n: "01", title: "Make it work, then make it honest.", body: "Build the eval before you optimize. Most of my so-called speed-ups began as fixes for a metric that was lying." },
      { n: "02", title: "Distributed = retry-safe by default.", body: "Idempotency keys, dedup at the consumer, no side effects in a step you might run twice. Assume the network hates you." },
      { n: "03", title: "Latency is a budget, not a hope.", body: "Pick the p95 first. Carve a slice for every stage. Defend each slice in code, not in the design doc." },
      { n: "04", title: "Logs > dashboards > meetings.", body: "If a system can't explain its own failure in plaintext, you don't really run it. Structured logs first, fancy graphs second." },
    ],
  },

  reading: {
    nowReading: [
      { title: "The Annotated Transformer (rev. 2024)", author: "Sasha Rush et al.", note: "Re-reading. The clearest mental model I have for attention." },
      { title: "Sparse, Dense, and Attentional Retrieval", author: "Karpukhin / Khattab", note: "Pre-req for everything I'm doing on hybrid RAG." },
      { title: "DSPy — compiling LLM programs", author: "Khattab et al.", note: "If you're still writing prompts by hand in 2026, please read this." },
    ],
    influences: [
      { title: "Programming Pearls", author: "Jon Bentley" },
      { title: "Deep Learning", author: "Goodfellow, Bengio, Courville" },
      { title: "Pattern Recognition and Machine Learning", author: "Bishop" },
      { title: "The Pragmatic Programmer", author: "Hunt & Thomas" },
      { title: "Designing Data-Intensive Applications", author: "Kleppmann" },
      { title: "Probabilistic Machine Learning", author: "Murphy" },
    ],
    listening: ["Latent Space", "The Gradient", "Lex Fridman — when the guest is a builder"],
  },
};
